import {createAction, handleActions} from 'redux-actions';
import { matchPath } from 'react-router-dom';
import { getLocation } from 'connected-react-router';
import { store } from 'components/App/Context';

import * as commonActionHandlers from 'utils/redux/commonActionHandlers';
import getConstantsByFeatureName from '../../utils/redux/getConstantsByFeatureName';
import getActionCreators from '../../utils/redux/getActionCreators';
import getActionHandlers from '../../utils/redux/getActionHandlers';
import getSelectorByFeatureName from '../../utils/redux/getSelectorByFeatureName';
import getCRUDSagas from '../../utils/redux/getCRUDSagas';

import Api from '../../utils/resourcesApi';

import {
  actionHandlers as approveActionHandlers,
  initialState as approveInitialState
} from './approve/redux';

import {
  actionHandlers as fillFromInvoiceActionHandlers,
  initialState as fillFromInvoiceInitialState
} from './fill-from-invoice/redux';

import {
  actionHandlers as linkedInvoicesActionHandlers,
  initialState as linkedInvoicesInitialState
} from './linked-invoices/redux';

import {
  actionHandlers as mailerActionHandlers,
  initialState as mailerInitialState
} from '../components/mailers/purchase_order/redux'
import {call, put, select, takeEvery} from "@redux-saga/core/effects";

// FIN-1283: the list is paged on the server and reloads when the toolbar settings it is
// filtered by change — the companies, the (shared, FIN-1142) budget year and the period.
export const RELOAD_ON = [
  '@SETTINGS/GLOBAL/SET_PO_COMPANIES',
  '@SETTINGS/GLOBAL/SET_BUDGET_YEAR',
  '@SETTINGS/PURCHASE_ORDERS/SET_PERIOD_TYPE',
];

export const constants = {
  ...getConstantsByFeatureName('PURCHASE_ORDERS'),
  RELOAD_ON,
  SET_LOADING_ITEM_ATTACHMENTS: '@PURCHASE_ORDERS/SET_LOADING_ITEM_ATTACHMENTS',
  LOAD_ITEM_ATTACHMENTS_REQUEST: '@PURCHASE_ORDERS/LOAD_ITEM_ATTACHMENTS_REQUEST',
  LOAD_ITEM_ATTACHMENTS_SUCCESSFUL: '@PURCHASE_ORDERS/LOAD_ITEM_ATTACHMENTS_SUCCESSFUL',
  CLEAR_ITEM_ATTACHMENTS: '@PURCHASE_ORDERS/CLEAR_ITEM_ATTACHMENTS',
  DELETE_ATTACHMENT_REQUEST: '@PURCHASE_ORDERS/DELETE_ATTACHMENT_REQUEST',
  // FIN-1308: the form opened from an imported invoice starts from the document.
  PREFILL_FROM_IMPORT: '@PURCHASE_ORDERS/PREFILL_FROM_IMPORT',
  CLEAR_PREFILL: '@PURCHASE_ORDERS/CLEAR_PREFILL',
};

// FIN-1283: the sort and the search are remembered across visits, as the GraphQL list
// remembered them (PO_SORT / SEARCH_QUERY). A sort saved by that list named a browser
// column (`sortColumn`), not a server field, and is ignored.
const rememberedSort = () => {
  const saved = store.poSorting;
  return saved && typeof saved.field === 'string' && saved.direction
    ? { field: saved.field, direction: saved.direction }
    : commonActionHandlers.initialState.sort;
};

const baseInitialState = {
  ...commonActionHandlers.initialState,
  sort: rememberedSort(),
  search: store.searchQuery || '',
  itemAttachments: [],
  itemAttachmentsLoading: false,
  prefill: null,
};

const baseActionHandlers = {
  ...getActionHandlers(constants),
  // A column clicked for the first time sorts ascending, as it always did; the same
  // column clicked again turns around.
  [constants.SORT_ITEMS]: (state, action) => {
    const next = commonActionHandlers.sortItems(state, action);
    if (action.payload !== state.sort.field) next.sort = { field: action.payload, direction: 'asc' };
    store.poSorting = next.sort;
    return next;
  },
  [constants.CHANGE_SEARCH]: (state, action) => {
    store.searchQuery = action.payload;
    return commonActionHandlers.changeSearch(state, action);
  },
  [constants.CLEAR_SEARCH]: (state, action) => {
    store.searchQuery = '';
    return commonActionHandlers.clearSearch(state, action);
  },
  [constants.LOAD_ITEM_ATTACHMENTS_REQUEST]: (state) => ({
    ...state,
    itemAttachments: []
  }),
  [constants.SET_LOADING_ITEM_ATTACHMENTS]: (state) => ({
    ...state,
    itemAttachmentsLoading: true
  }),
  [constants.LOAD_ITEM_ATTACHMENTS_SUCCESSFUL]: (state, { payload }) => ({
    ...state,
    itemAttachmentsLoading: false,
    itemAttachments: payload
  }),
  [constants.CLEAR_ITEM_ATTACHMENTS]: (state) => ({
    ...state,
    itemAttachments: []
  }),
  [constants.PREFILL_FROM_IMPORT]: (state, { payload }) => ({ ...state, prefill: payload }),
  [constants.CLEAR_PREFILL]: (state) => ({ ...state, prefill: null }),
}


export const actions = {
  ...getActionCreators(constants),
  setLoadingItemAttachments: createAction(constants.SET_LOADING_ITEM_ATTACHMENTS),
  loadItemAttachmentsRequest: createAction(constants.LOAD_ITEM_ATTACHMENTS_REQUEST),
  loadItemAttachments: createAction(constants.LOAD_ITEM_ATTACHMENTS_SUCCESSFUL),
  clearItemAttachments: createAction(constants.CLEAR_ITEM_ATTACHMENTS),
  deleteAttachmentRequest: createAction(constants.DELETE_ATTACHMENT_REQUEST),
  prefillFromImport: createAction(constants.PREFILL_FROM_IMPORT),
  clearPrefill: createAction(constants.CLEAR_PREFILL),
}


export const actionHandlers = {
  ...baseActionHandlers,
  ...approveActionHandlers,
  ...fillFromInvoiceActionHandlers,
  ...linkedInvoicesActionHandlers,
  ...mailerActionHandlers,
};

export const initialState = {
  ...baseInitialState,
  approve: approveInitialState,
  fillFromInvoice: fillFromInvoiceInitialState,
  linkedInvoices: linkedInvoicesInitialState,
  mailer: mailerInitialState,
};

export const reducer = handleActions(actionHandlers, initialState);

export const selector = getSelectorByFeatureName('purchaseOrders');

const baseSagas = getCRUDSagas('/purchase_orders', Api.purchaseOrders, actions, constants, 'purchaseOrders', 'purchaseOrders');

// FIN-1283: reload the list from its first page when its toolbar settings change — only
// while the user is on it; the budget year is shared with the RL tab (FIN-1142) and the
// list loads itself when opened.
export function* reloadList() {
  const { pathname } = yield select(getLocation);
  if (!matchPath(pathname, { path: '/purchase_orders' })) return;

  yield put(actions.applyFilter());
}

const extSagas = () => {

  function* loadItemAttachments({payload: {id, model, withoutLoading = false}}) {
    try {
      if (!id) return;
      if (!withoutLoading) yield put(actions.setLoadingItemAttachments(true));
      const data = yield call(Api.attachments.index, {record_id: id, record_type: model})
      yield put(actions.loadItemAttachments(data.data))
    } catch (e) {
      console.error(e);
    }
  }

  function* deleteAttachment({payload: {itemId, id, model}}) {
    try {
      if (!itemId) return;
      yield call(Api.attachments.delete, itemId)
      yield loadItemAttachments({payload: {id, model}})
    } catch (e) {
      console.error(e);
    }
  }

  return [
    takeEvery(constants.LOAD_ITEM_ATTACHMENTS_REQUEST, loadItemAttachments),
    takeEvery(constants.DELETE_ATTACHMENT_REQUEST, deleteAttachment),
    takeEvery(RELOAD_ON, reloadList),
  ]
}

export const sagas = () => [
  ...baseSagas(),
  ...extSagas(),
]
